import React, { useState, useEffect } from 'react';
import PageContainer from '../components/containers/PageContainer';
import { IconButton, TableCell, TableRow, Tooltip, Stack, Button } from '@mui/material';
import { toast } from 'react-toastify';
import CustomTable from '../components/ui/CustomTable';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ModalContainer from '../components/containers/ModalContainer';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Link } from 'react-router-dom';
import TicketModal from '../components/modals/TicketModal';
import api from '../config/axios';
import StatusComp from '../components/common/StatusComp';

const Tickets = () => {
    const [tickets, setTickets] = useState([]);
    const [alert, setAlert] = useState('');
    const [loader, setLoader] = useState('');
    const [filter, setFilter] = useState('All');
    const [sort, setSort] = useState('asc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState();
    const [total, setTotal] = useState(0);
    const [ticket, setTicket] = useState({});
    const [updateLoader, setUpdateLoader] = useState('');
    const [updateMessage, setUpdateMessage] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const [showTicketUpdate, setShowTicketUpdate] = useState(false);

    const onCloseTicketUpdateModal = () => {
        setShowTicketUpdate(false);
    };
    const onOpenTicketUpdateModal = () => setShowTicketUpdate(true);

    const [showTicketReply, setShowTicketReply] = useState(false);

    const onCloseTicketReplyModal = () => {
        setShowTicketReply(false);
    };
    const onOpenTicketReplyModal = () => setShowTicketReply(true);

    const [showTicketDelete, setShowTicketDelete] = useState(false);

    const onCloseTicketDeleteModal = () => {
        setShowTicketDelete(false);
    };
    const onOpenTicketDeleteModal = () => setShowTicketDelete(true);

    const getTickets = async () => {
        setUpdateLoader(true);
        await api
            .get(`/admin/tickets/read?status=${filter}&sort=${sort}&page=${page}&limit=${limit}`)
            .then((res) => {
                setUpdateLoader(false);
                setTickets(res.data.tickets);
                setTotalPages(res.data.totalPages);
                setTotal(res.data.total);
            })
            .catch((err) => {
                setUpdateLoader(false);
                console.log(err);
            });
    };

    useEffect(() => {
        getTickets();
    }, [page, limit, filter, sort]);

    const updateTicket = async (e) => {
        e.preventDefault();
        setUpdateLoader(true);

        const data = {
            status: e.target.status.value,
        };

        await api
            .put('/admin/tickets/update/' + ticket._id, data)
            .then((res) => {
                setUpdateLoader(false);
                getTickets();
                toast.success(res.data.message);
                onCloseTicketUpdateModal();
            })
            .catch((err) => {
                toast.error(err.response.data.message);
                setUpdateLoader(false);
            });

        setTimeout(() => {
            setUpdateMessage('');
            e.target.reset();
        }, 3000);
    };

    const deleteTicket = async (e) => {
        e.preventDefault();
        setUpdateLoader(true);
        await api
            .delete('/admin/tickets/delete/' + ticket._id)
            .then((res) => {
                setUpdateLoader(false);
                getTickets();
                toast.success(res.data.message);
                onCloseTicketDeleteModal();
            })
            .catch((err) => {
                toast.error(err.response.data.message);
                setUpdateLoader(false);
            });

        setTimeout(() => {
            setUpdateMessage('');
            e.target.reset();
        }, 3000);
    };

    return (
        <PageContainer title="View Tickets" desc="Resolve and manage all your customer support tickets">
            <CustomTable
                sx={{ mt: 2 }}
                fields={['ORDER', 'SUBJECT', 'USERNAME', 'STATUS', 'LAST RESPONSE', 'CREATED AT', 'ACTION']}
                dense={false}
                pagination
                count={totalPages}
                page={page}
                setPage={setPage}>
                {tickets.map((ticket, index) => (
                    <TableRow key={index}>
                        <TableCell
                            sx={{
                                color: 'primary.main',
                            }}>
                            <Link to="">{ticket.order_uuid || 'N/A'}</Link>
                        </TableCell>
                        <TableCell>{ticket.subject}</TableCell>
                        <TableCell
                            sx={{
                                color: 'primary.main',
                            }}>
                            {ticket?.user?.username}
                        </TableCell>
                        <TableCell>
                            <StatusComp status={ticket.status} />
                        </TableCell>
                        <TableCell>
                            {ticket.lastMessage ? new Date(ticket.lastMessage).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell>{new Date(ticket.createdAt).toDateString()}</TableCell>
                        <TableCell>
                            <Stack direction="row">
                                <Tooltip title="Edit Status">
                                    <IconButton
                                        onClick={() => {
                                            setTicket(ticket);
                                            onOpenTicketUpdateModal();
                                        }}>
                                        <ModeEditOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reply">
                                    <IconButton
                                        onClick={() => {
                                            setTicket(ticket);
                                            onOpenTicketReplyModal();
                                        }}>
                                        <ReplyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <IconButton
                                    onClick={() => {
                                        setTicket(ticket);
                                        onOpenTicketDeleteModal();
                                    }}>
                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </CustomTable>
            {/* Update Ticket */}
            <ModalContainer
                hideActions
                open={showTicketUpdate}
                onClose={onCloseTicketUpdateModal}
                title="Update Ticket">
                {updateMessage}

                <form onSubmit={updateTicket} className="form-validate is-alter">
                    <div className="form-group">
                        <label className="form-label" htmlFor="full-name">
                            Status
                        </label>

                        <select
                            name="status"
                            id=""
                            className="form-control"
                            value={ticket.status}
                            onChange={(e) => {
                                setTicket({ ...ticket, status: e.target.value });
                            }}>
                            <option value="waiting for customer response">Waiting for Customer Response</option>
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>

                    <div className="form-group mt-2 mb-2 align-left">
                        <Button variant="contained" type="submit">
                            Update {updateLoader}
                        </Button>
                    </div>
                </form>
            </ModalContainer>
            {/* Delete Ticket */}

            <ModalContainer
                hideActions
                open={showTicketDelete}
                onClose={onCloseTicketDeleteModal}
                title="Delete Ticket">
                <form onSubmit={deleteTicket} className="form-validate is-alter">
                    Are you sure want to delete this ticket?
                    <div className="form-group mt-2">
                        <button type="submit" className="btn btn-sm btn-danger p-1 cursor-pointer">
                            Delete {updateLoader}
                        </button>
                    </div>
                </form>
            </ModalContainer>
            <TicketModal
                open={showTicketReply}
                onClose={onCloseTicketReplyModal}
                ticket={ticket}
                setTicket={setTicket}
            />
        </PageContainer>
    );
};

export default Tickets;
